// Message quick action — the jump to the conversation.
//
// The Text branch of the channel chooser does not open a composer. The
// conversation is already on the page with one of its own, and a second
// would be a second draft to lose; this takes the agent to it.
//
// Opening the section first matters: it is collapsible, and a scroll to
// something inside a closed <details> lands on the header with nothing
// under it.
//
// Targets: [data-jump-to-thread], whose value is a selector. An
// optional [data-close-modal] names a modal to dismiss on the way, for
// the copy of this control that lives inside one.

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

// The stylesheet's reduced-motion block cannot reach a scroll started
// from script, and a smooth run down a long detail page is exactly the
// sustained motion that setting asks us to stop.
const scrollBehavior = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ? "auto"
    : "smooth";

export function initMessageChannel(root = document) {
  root.querySelectorAll("[data-jump-to-thread]").forEach((trigger) => {
    if (!firstBind(trigger)) return;

    trigger.addEventListener("click", () => {
      // Closed first, and unconditionally. A miss below would otherwise
      // leave the menu hanging over a page where nothing happened.
      trigger.closest("details[data-close-outside]")?.removeAttribute("open");
      document.getElementById(trigger.dataset.closeModal)?.remove();

      const thread = document.querySelector(trigger.dataset.jumpToThread);
      if (!thread) return;

      const section = thread.closest("details");
      if (section) section.open = true;

      // Scroll to the composer, not to the thread: the thread is tall
      // and scrolls inside itself, so centring it can leave the box the
      // caret just landed in below the fold.
      const composer = thread.querySelector("textarea");
      (composer ?? thread).scrollIntoView({
        block: "center",
        behavior: scrollBehavior(),
      });
      composer?.focus({ preventScroll: true });
    });
  });
}
